import React, { useState, useEffect, useContext } from 'react';
import { Modal, Box, Typography, TextField, List, ListItem, ListItemText, IconButton, Grid, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser, selectIndicatorUsers, getUserIndicators } from '../../redux/user/user.actions';
import { getServiceGroups } from '../../redux/serviceGroup/serviceGroup.actions';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { Sort } from '@material-ui/icons';

function UserTransferModal({ open, handleClose, title, operatorAction, actionType, departmentAction, showDepartments }) {
  const [searchTerm, setSearchTerm] = useState('');
  const indicatorUser = useSelector(selectIndicatorUsers);
  const currentUser = useSelector(selectCurrentUser);
  const serviceGroups = useSelector(state => state.serviceGroups?.serviceGroups) || [];
  const enabledGroups = serviceGroups.filter(g => g.queue_enabled);
  const [deptConfirm, setDeptConfirm] = useState({ open: false, group: null });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [order, setOrder] = useState('asc');
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const { theme } = useContext(ThemeContext);
  const crmId = useSelector(state => state.user.currentUser?.CRM_id);
  const dispatch = useDispatch();

  // Sempre que o modal abre, busca a lista de operadores atualizada (status de chat
  // em tempo real), pra não depender de refresh da página.
  useEffect(() => {
    if (open) {
      dispatch(getUserIndicators());
      if (showDepartments) {
        dispatch(getServiceGroups());
      }
    }
  }, [open, dispatch, showDepartments]);

  const handleOpenConfirmModal = (isConfirm, user) => {
    setConfirmModal(isConfirm);
    setSelectedUser(user);
  }

  // Clique num departamento: o backend escolhe o operador; se não houver ninguém
  // disponível, pede confirmação para mandar à fila.
  const handleDepartmentClick = async (group) => {
    if (!departmentAction) return;
    const res = await departmentAction(group.id, false);
    if (res?.result === 'needs_confirmation') {
      setDeptConfirm({ open: true, group });
    }
  };

  useEffect(() => {
    const sortedUsers = [...indicatorUser].sort((a, b) => {
      if (order === 'asc') {
        return a.total - b.total;
      } else {
        return b.total - a.total;
      }
    }).filter(user => user.user_name.toLowerCase().includes(searchTerm.toLowerCase()));

    setFilteredUsers(sortedUsers);
  }, [searchTerm, indicatorUser, order]);

  return (
    <Modal open={open} onClose={handleClose} sx={{ overflowY: 'auto' }}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        maxHeight: '90vh',
        bgcolor: theme.palette.background.default,
        border: 'none',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        p: 4,
        overflowY: 'auto',
        '&:focus-visible': {
          outline: 'none'
        }
      }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
          {title}
        </Typography>
        {showDepartments && enabledGroups.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Departamentos (atribui automaticamente ao menos ocupado)
            </Typography>
            <List sx={{ pt: 0 }}>
              {enabledGroups.map((group) => (
                <ListItem button key={`dept-${group.id}`} onClick={() => handleDepartmentClick(group)} sx={{
                  mb: 1,
                  borderRadius: '4px',
                  bgcolor: theme.palette.button.primary,
                  '&:hover': { bgcolor: theme.palette.background.hover },
                  color: theme.palette.text.button,
                }}>
                  <ListItemText primary={group.description} primaryTypographyProps={{ fontWeight: 'medium' }} />
                </ListItem>
              ))}
            </List>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Ou transfira para um operador específico:
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          margin="normal"
          label="Pesquisar operador"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            sx: {
              borderRadius: '4px',
              bgcolor: 'background.default',
              '& fieldset': {
                transition: 'border-color 0.2s ease-in-out',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
          sx={{
            mb: 2,
          }}
        />
        <List sx={{ pt: 0 }}>
          {filteredUsers.map((user) => (
            currentUser.id !== user.id && Number(user.chat_status) === 1 && (
              <ListItem button key={user.id} onClick={() => handleOpenConfirmModal(true, user)} sx={{
                mb: 1,
                borderRadius: '4px',
                bgcolor: theme.palette.button.primary,
                '&:hover': {
                  bgcolor: theme.palette.background.hover,
                },
                color: theme.palette.text.button,
                transition: 'background-color 0.2s ease-in-out',
              }}>
                <ListItemText
                  primary={user.user_name}
                  secondary={`${user.service_group_name || 'Sem departamento'} • Atendimentos ativos: ${user.total}`}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                  secondaryTypographyProps={{ color: theme.palette.text.buttonCaption }}
                />
              </ListItem>
            )
          ))}
        </List>
        {confirmModal && (
          <Box
            sx={{
              mt: 2,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: theme.palette.background.messageBox,
              p: 2,
              borderRadius: 2,
              zIndex: 1,
              width: '70%',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
            }}
          >
            <Typography component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
              Deseja {title.includes('Convidar')
                ? ` convidar ${selectedUser.user_name} para o atendimento?`
                : `transferir o atendimento para ${selectedUser.user_name}?`
              }
            </Typography>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-around',
              '& button': {
                minWidth: 'auto',
                padding: 0,
                textTransform: 'none',
                fontWeight: 'medium',
                fontSize: '14px',
                lineHeight: '20px',
              }

            }}>
              <Button onClick={() => handleOpenConfirmModal(false, {})} sx={{ color: theme.palette.error.main }}>Não</Button>
              <Button onClick={() => operatorAction(selectedUser.id, actionType)} sx={{ color: theme.palette.success.main }}>Sim</Button>
            </Box>
          </Box>
        )}
        {deptConfirm.open && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: theme.palette.background.messageBox,
              p: 2,
              borderRadius: 2,
              zIndex: 2,
              width: '80%',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
            }}
          >
            <Typography component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
              Não há operadores disponíveis em “{deptConfirm.group?.description}” no momento. Deseja enviar para a fila mesmo assim?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', '& button': { textTransform: 'none', fontWeight: 'medium' } }}>
              <Button onClick={() => setDeptConfirm({ open: false, group: null })} sx={{ color: theme.palette.error.main }}>Não</Button>
              <Button
                onClick={async () => {
                  const g = deptConfirm.group;
                  setDeptConfirm({ open: false, group: null });
                  if (g) await departmentAction(g.id, true);
                }}
                sx={{ color: theme.palette.success.main }}
              >
                Sim, enviar para a fila
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default UserTransferModal;
